import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SubscriptionTierCard = ({
  tierKey,
  tier = 'free',
  name = 'Gratuit',
  description = '',
  recommendationMessage,
  features = [],
  isPopular = false,

  // Etat du plan
  isCurrentPlan = false,
  isCancelledAtPeriodEnd = false,
  isPendingTier = false,
  pendingDate,

  // Pricing
  trialDays = 0,
  hasFirstYearDiscount = false,
  monthlyPrice = 0,
  yearlyPrice = 0,
  billingPeriod = 'month', // 'month' | 'year'

  // Actions / CTA
  onSelect,
  onCancel,
  onReactivate,
  buttonText,
  buttonVariant = 'default',
  disabled = false,
}) => {
  const isYear = billingPeriod === 'year';

  const displayPrice = isYear
    ? (hasFirstYearDiscount && yearlyPrice ? `${yearlyPrice / 2} $` : `${yearlyPrice} $`)
    : `${monthlyPrice} $`;

  const displayPeriod = isYear ? 'CAD/année' : 'CAD/mois';

  const displayOriginalYearlyPrice =
    isYear && hasFirstYearDiscount && yearlyPrice ? `${yearlyPrice} $` : null;

  const savingsPercent =
    isYear && hasFirstYearDiscount && yearlyPrice ? 50 : null;

  // Badge "Recommandé" seulement si populaire ET pas le plan actuel
  const showPopular = !!isPopular && !isCurrentPlan;

  return (
    <div
      className="relative rounded-2xl border p-6 md:p-8 flex flex-col h-full"
      style={{
        background: 'var(--color-card)',
        borderColor: showPopular ? 'var(--color-primary)' : 'var(--color-border)',
        boxShadow: showPopular ? '0 0 0 1px var(--color-primary)' : 'none',
      }}
    >
      {showPopular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <div
            className="px-3 py-1 rounded-full text-xs font-semibold"
            style={{
              background: 'var(--color-primary)',
              color: 'white',
              fontFamily: 'var(--font-caption)',
            }}
          >
            Recommandé
          </div>
        </div>
      )}

      {/* Header */}
      <div className="mb-4">
        <h3
          className="text-xl md:text-2xl font-bold"
          style={{ fontFamily: 'var(--font-heading)', color: 'var(--color-foreground)' }}
        >
          {name}
        </h3>

        {recommendationMessage && (
          <p className="mt-1 text-sm" style={{ color: 'var(--color-muted-foreground)' }}>
            {recommendationMessage}
          </p>
        )}

        {!!description && (
          <p className="mt-2 text-sm" style={{ color: 'var(--color-muted-foreground)' }}>
            {description}
          </p>
        )}
      </div>

      {/* Prix */}
      <div className="mb-5">
        <div className="flex items-end gap-2">
          <div
            className="text-4xl font-bold"
            style={{ fontFamily: 'var(--font-heading)', color: 'var(--color-foreground)' }}
          >
            {displayPrice}
          </div>
          <div className="pb-1 text-sm" style={{ color: 'var(--color-muted-foreground)' }}>
            {displayPeriod}
          </div>
        </div>

        {displayOriginalYearlyPrice && (
          <div className="mt-1 flex items-center gap-2">
            <span className="text-sm line-through" style={{ color: 'var(--color-muted-foreground)' }}>
              {displayOriginalYearlyPrice}
            </span>

            {savingsPercent && (
              <span
                className="text-xs font-semibold px-2 py-0.5 rounded-full"
                style={{ background: 'rgba(34,197,94,0.15)', color: 'var(--color-success)' }}
              >
                -{savingsPercent}%
              </span>
            )}
          </div>
        )}

        {trialDays && !isCurrentPlan && (
          <div className="mt-2 text-xs" style={{ color: 'var(--color-muted-foreground)' }}>
            Essai gratuit limité par usage
          </div>
        )}

        {isCancelledAtPeriodEnd && pendingDate && (
          <div className="mt-2 text-xs" style={{ color: 'var(--color-warning)' }}>
            Annulation prévue le {pendingDate}
          </div>
        )}

        {isPendingTier && pendingDate && (
          <div className="mt-2 text-xs" style={{ color: 'var(--color-primary)' }}>
            Changement prévu le {pendingDate}
          </div>
        )}
      </div>

      {/* Features */}
      <ul
        className="flex-1 space-y-3 md:space-y-4"
        style={{ marginBottom: 'var(--spacing-lg)' }}
      >
        {features?.map((feature, index) => (
          <li key={index} className="flex items-start gap-3">
            <Icon
              name={feature?.included ? 'Check' : 'X'}
              size={20}
              color={feature?.included ? 'var(--color-success)' : 'var(--color-muted-foreground)'}
              className="flex-shrink-0 mt-0.5"
            />
            <span
              className="text-sm"
              style={{
                color: feature?.included ? 'var(--color-foreground)' : 'var(--color-muted-foreground)',
                fontFamily: 'var(--font-body)',
                lineHeight: 1.5,
              }}
            >
              {feature?.text}
            </span>
          </li>
        ))}
      </ul>

      {/* CTA + Actions */}
      <div className="mt-auto">
        <Button
          fullWidth
          variant={buttonVariant}
          disabled={disabled}
          // ✅ IMPORTANT: ne jamais forward l'event, et garde la signature (tierKey, billingPeriod)
          onClick={() => onSelect?.(tierKey, billingPeriod)}
        >
          {buttonText || 'Choisir'}
        </Button>

        {isCurrentPlan && !isCancelledAtPeriodEnd && (
          <div className="mt-3 flex gap-2">
            <Button fullWidth variant="outline" onClick={onCancel}>
              Mettre en pause
            </Button>
          </div>
        )}

        {isCurrentPlan && isCancelledAtPeriodEnd && (
          <div className="mt-3">
            <Button fullWidth variant="default" onClick={onReactivate}>
              Réactiver
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionTierCard;
